import React from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import IntlComponent from 'axway-react-intl';
import { Col, ControlLabel, FormControl, FormGroup, HelpBlock, InputGroup } from 'react-bootstrap';
import { ModalDialog, Form } from 'axway-react-ui';

class ModelInfoDialogContent extends ModalDialog.Content {
	constructor(props) {
		super(props);
		IntlComponent.init(this);
		this.onChangeField = this.onChangeField.bind(this);
		this.validateModelName = this.validateModelName.bind(this);

		this.registerFieldValidation('modelName', this.validateModelName);
		this.registerFieldValidation('modelParent');
		this.registerFieldValidation('modelConnector');
		this.registerFieldValidation('modelDescription');
	}

	componentWillMount() {
		// If there's no value selected for the lists then emit the first as a "change"
		// to ensure the parent state is correct.
		const update = {};

		if (typeof this.props.values.modelParent === 'object') {
			update.modelParent = this.props.values.modelParent.name;
		} else if (this.props.mode === 'compose' && !this.props.values.modelParent) {
			update.modelParent = Object.keys(this.props.models).sort()[0];
			update.requestModelParent = true;
		}

		if ((this.props.mode === 'create' || this.props.mode === 'edit') && !this.props.values.modelConnector) {
			update.modelConnector = this.props.connectors[0].name;
		}

		// Push the initial values into the state.
		this.setState({
			...this.props.values,
			...update,
			initial: { // Remember what we start with for validation
				...this.props.values,
				...update
			}
		});
	}

	componentDidMount() {
		this.changed();
	}

	onChangeField(name, value) {
		this.setFieldState(name, value, () => {
			this.changed();
		});
	}

	validateModelName() {
		const modelName = this.state.modelName && this.state.modelName.toLowerCase();
		this.validationMsgs = this.validationMsgs || {};
		this.validationMsgs.modelName = null;

		if (modelName === undefined || modelName === '') {
			this.validationMsgs.modelName = this.intl.formatMsg('The model name is required.');
			return false;
		}
		if (modelName.indexOf(' ') !== -1) {
			this.validationMsgs.modelName = this.intl.formatMsg('The model name cannot contain spaces.');
			return false;
		}
		const reg = /^[a-z][a-z\d-]*$/;
		if (modelName.toLowerCase().match(reg) === null) {
			this.validationMsgs.modelName = this.intl.formatMsg('The model name must start with a letter and contain only alphanumerics or -.');
			return false;
		}

		const { initial } = this.state;
		const initialModelName = initial.modelName && initial.modelName.toLowerCase();
		if (initialModelName !== modelName
				&& Object.keys(this.props.models).find(m => m.toLowerCase() === modelName)) {
			this.validationMsgs.modelName = this.intl.formatMsg('A model with this name already exists.');
			return false;
		}

		return true;
	}

	changed() {
		const { onChange } = this.props;
		if (!onChange) {
			return;
		}
		const fields = {
			modelName: this.state.modelName,
			modelConnector: this.state.modelConnector,
			modelDescription: this.state.modelDescription,
			modelParent: this.state.modelParent
		};
		onChange(fields);
	}

	renderErrors(fieldName, fieldNameProps) {
		if (fieldNameProps.validationState === 'error') {
			return (
				<HelpBlock>
					{ this.validationMsgs[fieldName] }
				</HelpBlock>
			);
		}

		return null;
	}

	render() {
		const { modelConnector, modelName, modelDescription, modelParent, validate } = this.state;
		const modelNameValidationProps = this.getFieldValidationProps('modelName');

		return (
			<Form	horizontal className="edit-model" onSubmit={this.props.doValidate}>
				{!this.state.requestModelParent && modelConnector === 'composite' && (
					<FormGroup controlId="parentCtrl" className="model-parent-group">
						<Col componentClass={ControlLabel} sm={3}>
							{this.intl.formatMsg('Parent model')}:
						</Col>
						<Col sm={9}>
							<FormControl.Static>
								{modelParent}
							</FormControl.Static>
						</Col>
					</FormGroup>
				)}

				{this.state.requestModelParent && (
					<FormGroup controlId="parentCtrl" className="model-parent-group">
						<Col componentClass={ControlLabel} sm={3}>
							{this.intl.formatMsg('Parent model')}:
						</Col>
						<Col sm={9}>
							<FormControl
								className="model-parent"
								componentClass="select"
								placeholder="select"
								onChange={(e) => this.onChangeField('modelParent', e.target.value)}
								value={modelParent || ''}
							>
								{
									Object.keys(this.props.models).sort().map((name) => {
										return <option key={name} value={name}>{name}</option>;
									})
								}
							</FormControl>
						</Col>
					</FormGroup>
				)}

				<FormGroup controlId="nameCtrl" className="model-name-group" {...modelNameValidationProps}>
					<Col componentClass={ControlLabel} sm={3}>
						{this.intl.formatMsg('Model name')}:
					</Col>
					<Col sm={9}>
						{ this.props.mode === 'edit' && (
							<FormControl.Static>
								{modelName}
							</FormControl.Static>
						)}
						{ this.props.mode !== 'edit' && (
							<InputGroup>
								<FormControl
									type="text"
									className="model-name"
									autoComplete="off"
									autoCorrect="off"
									spellCheck="off"
									placeholder={`(${this.intl.formatMsg('required')})`}
									onChange={(e) => this.onChangeField('modelName', e.target.value)}
									value={modelName || ''}
								/>
								<InputGroup.Addon>*</InputGroup.Addon>
							</InputGroup>
						)}
						{validate && this.renderErrors('modelName', modelNameValidationProps)}
					</Col>
				</FormGroup>

				<FormGroup controlId="typeCtrl" className="model-type-group">
					<Col componentClass={ControlLabel} sm={3}>
						{this.intl.formatMsg('Connector')}:
					</Col>
					<Col sm={9}>
						{ this.props.mode !== 'create' && (
							<FormControl.Static>
								{modelConnector}
							</FormControl.Static>
						)}
						{ this.props.mode === 'create' && (
							<FormControl
								className="model-type"
								componentClass="select"
								placeholder="select"
								onChange={(e) => this.onChangeField('modelConnector', e.target.value)}
								value={modelConnector || ''}
								disabled={this.props.mode === 'compose'}
							>
								{
									this.props.connectors.filter(c => c.name !== 'composite').map(
										(value) => (
											<option
												key={value.name}
												value={value.name}
											>
												{value.name}
											</option>
										)
									)
								}
							</FormControl>
						)}
					</Col>
				</FormGroup>

				<FormGroup controlId="descriptionCtrl" className="model-description-group">
					<Col componentClass={ControlLabel} sm={3}>
						{this.intl.formatMsg('Description')}:
					</Col>
					<Col sm={9}>
						<FormControl
							className="model-description"
							componentClass="textarea"
							placeholder="description..."
							onChange={(e) => this.onChangeField('modelDescription', e.target.value)}
							value={modelDescription || ''}
						/>
					</Col>
				</FormGroup>
			</Form>
		);
	}
}

ModelInfoDialogContent.propTypes = {
	connectors: PropTypes.array.isRequired,
	values: PropTypes.shape({
		modelParent: PropTypes.oneOfType([
			PropTypes.string,
			PropTypes.object
		]),
		modelName: PropTypes.string,
		modelConnector: PropTypes.string,
		modelDescription: PropTypes.string
	}),
	models: PropTypes.object.isRequired,
	mode: PropTypes.string.isRequired,
	onChange: PropTypes.func,

	/**
	 * Submit action to be called on Return key pressed
	 */
	onSubmit: PropTypes.func
};

ModelInfoDialogContent.defaultProps = {
	values: {
		modelName: '',
		modelDescription: '',
		modelConnector: ''
	}
};

export default injectIntl(ModelInfoDialogContent);
